import { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "./context/ThemeContext";
import { Menu, X } from "lucide-react";

const NAV_ITEMS = [
  { label: "About", href: "#aboutme" },
  { label: "Skills", href: "#skills" },
  { label: "Career", href: "#career" },
  { label: "Projects", href: "#projects" },
  { label: "Contact", href: "#contact" },
];

const navItemStyles = `
    uppercase font-mono font-bold tracking-[0.15em]
    cursor-pointer transition-all duration-300 
    hover:text-cyan-400 
    dark:hover:text-cyan-300
    dark:hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]
  `;

const Navbar = () => {
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const handleOutsideClick = useCallback((e) => {
    if (menuRef.current && !menuRef.current.contains(e.target)) {
      setOpen(false);
    }
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [open, handleOutsideClick, handleKeyDown]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 flex justify-center p-4 w-full">
      <div
        className="relative flex items-center justify-between w-full max-w-4xl px-6 py-3 
                      backdrop-blur-2xl bg-white/60 dark:bg-white/5
                      border border-white/20 dark:border-white/10 
                      rounded-full shadow-lg transition-all duration-300">
        <button
          className="md:hidden text-gray-700 dark:text-white"
          onClick={() => setOpen(!open)}>
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
        <button onClick={toggleTheme} className="text-lg cursor-pointer">
          {theme === "dark" ? "🌙" : "☀️"}
        </button>
        <ul className="hidden md:flex items-center gap-6 text-sm text-gray-600 dark:text-cyan-400">
          {NAV_ITEMS.map((item) => (
            <li key={item.href} className={navItemStyles}>
              <a href={item.href}>{item.label}</a>
            </li>
          ))}
        </ul>
        <div
          ref={menuRef}
          className={`
              absolute top-full left-0 right-0 mt-4 mx-4 z-50 md:hidden
              rounded-3xl p-6
              bg-white/95 dark:bg-black/80
              backdrop-blur-2xl
              border border-white/30 dark:border-white/10
              shadow-xl
              transform transition-all duration-300 ease-out
              ${
                open
                  ? "opacity-100 translate-y-0 scale-100"
                  : "opacity-0 -translate-y-4 scale-95 pointer-events-none"
              }
          `}>
          <ul className="flex flex-col gap-4 text-center text-sm text-gray-700 dark:text-cyan-300">
            {NAV_ITEMS.map((item) => (
              <li key={item.href}>
                <a
                  href={item.href}
                  className={navItemStyles}
                  onClick={() => setOpen(false)}>
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
